import { ipfsToHttp } from "@/lib/image-upload";
import { createFileRoute, Link } from "@tanstack/react-router";
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";
import { Footer } from "@/components/Footer";
import logoAsset from "@/assets/robotdex-logo.jpg.asset.json";
import nftBasic from "@/assets/nft-basic.jpg";
import nftRare from "@/assets/nft-rare.jpg";
import nftEpic from "@/assets/nft-epic.jpg";
import nftLegendary from "@/assets/nft-legendary.jpg";
import { Zap, Shield, Globe2, Sparkles, ArrowRight, Circle, Gem, Crown, Flame } from "lucide-react";

const RARITIES = [
  {
    tier: "Common",
    icon: Circle,
    accent: "text-slate-300",
    ring: "from-slate-400/30 to-slate-500/10",
    supply: "High supply",
    odds: "70%",
    desc: "Entry-level NFTs anyone can mint. Great for collectors starting out.",
    image: nftBasic,
  },
  {
    tier: "Rare",
    icon: Gem,
    accent: "text-cyan-300",
    ring: "from-cyan-400/40 to-cyan-500/10",
    supply: "Mid supply",
    odds: "20%",
    desc: "Limited-supply pieces with unique traits and stronger demand.",
    image: nftRare,
  },
  {
    tier: "Epic",
    icon: Flame,
    accent: "text-fuchsia-300",
    ring: "from-fuchsia-400/40 to-fuchsia-500/10",
    supply: "Low supply",
    odds: "8%",
    desc: "Rare art with special attributes - highly sought by traders.",
    image: nftEpic,
  },
  {
    tier: "Legendary",
    icon: Crown,
    accent: "text-amber-300",
    ring: "from-amber-400/50 to-amber-500/10",
    supply: "Ultra-low supply",
    odds: "2%",
    desc: "The rarest tier. One-of-a-few masterpieces with the deepest lore.",
    image: nftLegendary,
  },
] as const;

export const Route = createFileRoute("/")({
  component: Landing,
  head: () => ({
    meta: [
      { title: "RobotDex - Fully On-Chain NFT Marketplace" },
      { name: "description", content: "Create, mint, buy, sell and list NFTs on Robinhood Chain and all EVM networks. Fully on-chain. No middlemen." },
      { property: "og:title", content: "RobotDex - Fully On-Chain NFT Marketplace" },
      { property: "og:description", content: "Create, mint, buy, sell and list NFTs on Robinhood Chain and all EVM networks. Fully on-chain. No middlemen." },
    ],
    links: [
      { rel: "preload", as: "image", href: logoAsset.url, fetchPriority: "high" },
      { rel: "preload", as: "image", href: nftLegendary, fetchPriority: "high" },
      { rel: "preload", as: "image", href: nftEpic },
      { rel: "preload", as: "image", href: nftRare },
      { rel: "preload", as: "image", href: nftBasic },
    ],
  }),
});


function Landing() {
  const { data: featured } = useQuery({
    queryKey: ["featured-drops"],
    queryFn: async () => {
      const { data } = await supabase
        .from("nft_drops")
        .select("*")
        .order("created_at", { ascending: false })
        .limit(6);
      return data ?? [];
    },
    staleTime: 30_000,
  });

  return (
    <div>
      <div className="mx-auto max-w-7xl px-4 sm:px-6 py-8 sm:py-16">
        {/* Hero - bold typography, deep black, product-render feel */}
        <section className="relative overflow-hidden rounded-2xl border border-white/10 bg-black p-6 sm:p-12 lg:p-16">
          {/* subtle ambient glows */}
          <div className="absolute -top-32 -left-24 h-72 w-72 rounded-full bg-primary/20 blur-[120px]" />
          <div className="absolute -bottom-32 -right-24 h-72 w-72 rounded-full bg-fuchsia-500/15 blur-[120px]" />
          <div className="absolute inset-0 opacity-[0.04] [background-image:linear-gradient(to_right,white_1px,transparent_1px),linear-gradient(to_bottom,white_1px,transparent_1px)] [background-size:40px_40px]" />

          <div className="relative grid grid-cols-1 lg:grid-cols-[1.15fr_1fr] gap-8 lg:gap-10 items-center">
            {/* Left: bold headline */}
            <div className="text-center lg:text-left">
              <div className="inline-flex items-center gap-2 mb-5 sm:mb-6">
                <img src={logoAsset.url} alt="RobotDex logo" fetchPriority="high" decoding="async" className="h-9 w-9 sm:h-10 sm:w-10 rounded-lg object-cover" />
                <span className="text-lg sm:text-xl font-black tracking-tight text-white">RobotDex</span>
              </div>
              <h1 className="text-[2.5rem] leading-[1.02] sm:text-6xl lg:text-7xl font-black tracking-[-0.03em] text-white">
                On-Chain NFTs <br className="hidden sm:block" />
                &amp; Instant <span className="italic font-light">Drops</span>
              </h1>
              <div className="mt-6 sm:mt-8 inline-flex items-center rounded-full border border-white/15 px-5 py-2.5 text-sm sm:text-base text-white/70">
                NFTs Marketplace
              </div>
              <div className="mt-5 flex items-center gap-2 text-white/60 text-sm sm:text-base justify-center lg:justify-start">
                <Sparkles className="h-4 w-4" />
                <span>fully decentralized</span>
              </div>
              <div className="mt-7 sm:mt-8 flex flex-wrap gap-3 justify-center lg:justify-start">
                <Button asChild size="lg" className="bg-white text-black hover:bg-white/90 font-semibold">
                  <Link to="/market">Explore Market <ArrowRight className="ml-1 h-4 w-4" /></Link>
                </Button>
                <Button asChild size="lg" variant="outline" className="border-white/20 text-white hover:bg-white/5">
                  <Link to="/create">Create NFT</Link>
                </Button>
              </div>
            </div>

            {/* Right: stacked NFT card renders */}
            <div className="relative h-[280px] sm:h-[360px] lg:h-[420px] hidden sm:block">
              <div className="absolute right-[8%] top-[8%] w-[45%] aspect-[2/3] rounded-2xl overflow-hidden ring-1 ring-white/10 shadow-[0_30px_60px_-20px_rgba(0,0,0,0.8)] rotate-[6deg]">
                <img src={nftLegendary} alt="" fetchPriority="high" decoding="async" className="h-full w-full object-cover opacity-90" />
              </div>
              <div className="absolute right-[24%] top-[16%] w-[45%] aspect-[2/3] rounded-2xl overflow-hidden ring-1 ring-white/15 shadow-[0_30px_60px_-20px_rgba(0,0,0,0.9)] rotate-[-2deg]">
                <img src={nftEpic} alt="" decoding="async" className="h-full w-full object-cover" />
              </div>
              <div className="absolute right-[42%] top-[22%] w-[45%] aspect-[2/3] rounded-2xl overflow-hidden ring-1 ring-white/20 shadow-[0_40px_80px_-20px_rgba(139,92,246,0.5)] -rotate-[10deg]">
                <img src={nftRare} alt="" decoding="async" className="h-full w-full object-cover" />
              </div>
            </div>
          </div>
        </section>

        {/* Rarity tiers - single-line overlapping row, responsive on all widths */}
        <section className="mt-10 sm:mt-14">
          <div className="mb-5 sm:mb-6 text-center">
            <h2 className="text-2xl sm:text-3xl lg:text-4xl font-black tracking-tight">NFT rarity tiers</h2>
            <p className="text-muted-foreground text-sm mt-1">Common · Rare · Epic · Legendary</p>
          </div>
          <div className="relative overflow-hidden rounded-2xl border border-white/10 bg-gradient-to-br from-black via-background to-black px-1 sm:px-6 py-6 sm:py-12">
            <div className="absolute inset-0 pointer-events-none bg-[radial-gradient(circle_at_50%_50%,hsl(var(--primary)/0.15),transparent_60%)]" />
            <div className="relative flex items-center justify-center gap-0.5 sm:gap-3 md:gap-4">
              {RARITIES.map((r, i) => (
                <RarityCard key={r.tier} r={r} index={i} />
              ))}
            </div>
          </div>
        </section>




        {/* Features */}
        <section className="mt-12 sm:mt-16">
          <h2 className="text-2xl sm:text-3xl font-bold text-center">Built for creators & collectors</h2>
          <div className="mt-6 sm:mt-8 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
            <Feature icon={Zap} title="Instant Mint" desc="Deploy a drop in seconds. Limited or unlimited supply, timed sale windows." />
            <Feature icon={Shield} title="Fully On-Chain" desc="Ownership & royalties live entirely on the blockchain. You always own your keys." />
            <Feature icon={Globe2} title="Multi-Chain" desc="Robinhood Chain, Ethereum, Polygon, BSC, Arbitrum, Optimism, Base, Avalanche." />
            <Feature icon={Sparkles} title="Transparent Fees" desc="Flat platform fee set on-chain by Platform. Creator royalties paid on every trade." />
          </div>
        </section>

        {/* Featured drops */}
        <section className="mt-12 sm:mt-16">
          <div className="flex flex-wrap items-end justify-between gap-3 mb-4">
            <div className="min-w-0">
              <h2 className="text-2xl sm:text-3xl font-bold">Featured drops</h2>
              <p className="text-muted-foreground text-sm">Freshly launched by creators around the world.</p>
            </div>
            <Button asChild variant="ghost"><Link to="/market">View all →</Link></Button>
          </div>
          {featured && featured.length > 0 ? (
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3 sm:gap-4">
              {featured.map((d: any) => (
                <Link key={d.id} to="/drop/$id" params={{ id: d.id }} className="group">
                  <Card className="overflow-hidden bg-card/60 hover:neon-border transition">
                    <div className="aspect-square bg-gradient-to-br from-primary/20 to-background">
                      {d.logo_url ? (
                        <img src={ipfsToHttp(d.logo_url)} alt={d.name} loading="lazy" className="w-full h-full object-cover group-hover:scale-105 transition" />
                      ) : (
                        <div className="w-full h-full flex items-center justify-center text-2xl font-black neon-text">
                          {d.symbol?.slice(0, 3)}
                        </div>
                      )}
                    </div>
                    <div className="p-2">
                      <div className="text-sm font-semibold truncate">{d.name}</div>
                      <div className="text-xs text-muted-foreground truncate">{d.symbol}</div>
                    </div>
                  </Card>
                </Link>
              ))}
            </div>
          ) : (
            <Card className="p-8 sm:p-10 text-center neon-border bg-card/60">
              <p className="text-muted-foreground">No drops yet - be the first!</p>
              <Button asChild className="mt-4"><Link to="/create">Create a drop</Link></Button>
            </Card>
          )}
        </section>

        {/* FAQ */}
        <section className="mt-12 sm:mt-16 max-w-3xl mx-auto">
          <h2 className="text-2xl sm:text-3xl font-bold text-center mb-5 sm:mb-6">Frequently asked questions</h2>
          <Accordion type="single" collapsible className="w-full">
            <AccordionItem value="a">
              <AccordionTrigger className="text-left">What is RobotDex?</AccordionTrigger>
              <AccordionContent>
                RobotDex is a user-friendly, fully on-chain NFT marketplace. Anyone can create, mint, buy, sell, and list NFTs on Robinhood Chain and all major EVM networks.
              </AccordionContent>
            </AccordionItem>
            <AccordionItem value="b">
              <AccordionTrigger className="text-left">Which wallets are supported?</AccordionTrigger>
              <AccordionContent>
                All wallets supported by WalletConnect / Reown - MetaMask, Rainbow, Coinbase Wallet, Trust Wallet, Ledger, and 300+ others.
              </AccordionContent>
            </AccordionItem>
            <AccordionItem value="c">
              <AccordionTrigger className="text-left">How do fees work?</AccordionTrigger>
              <AccordionContent>
                A small platform fee is applied to every successful mint and trade. Creators also receive a royalty on every secondary sale. All fees are configurable on-chain by the Platform.
              </AccordionContent>
            </AccordionItem>
            <AccordionItem value="d">
              <AccordionTrigger className="text-left">Do I need an account?</AccordionTrigger>
              <AccordionContent>
                No. Your wallet is your account. Connect any EVM wallet to start creating and trading immediately.
              </AccordionContent>
            </AccordionItem>
            <AccordionItem value="e">
              <AccordionTrigger className="text-left">Which chains are supported?</AccordionTrigger>
              <AccordionContent>
                Robinhood Chain, Ethereum, Polygon, BNB Smart Chain, Arbitrum, Optimism, Base, Avalanche - with more added over time.
              </AccordionContent>
            </AccordionItem>
            <AccordionItem value="r-common">
              <AccordionTrigger className="text-left">What is a Common NFT?</AccordionTrigger>
              <AccordionContent>
                Common is the entry-level rarity - high supply, standard traits, and the most affordable mint price. Perfect for new collectors joining a drop.
              </AccordionContent>
            </AccordionItem>
            <AccordionItem value="r-rare">
              <AccordionTrigger className="text-left">What is a Rare NFT?</AccordionTrigger>
              <AccordionContent>
                Rare NFTs have mid supply and roll uncommon traits. They make up ~20% of a drop and hold stronger secondary-market value than Common pieces.
              </AccordionContent>
            </AccordionItem>
            <AccordionItem value="r-epic">
              <AccordionTrigger className="text-left">What is an Epic NFT?</AccordionTrigger>
              <AccordionContent>
                Epic pieces are low-supply NFTs with special visual attributes, accounting for roughly 8% of every drop. Highly sought by traders.
              </AccordionContent>
            </AccordionItem>
            <AccordionItem value="r-legendary">
              <AccordionTrigger className="text-left">What is a Legendary NFT?</AccordionTrigger>
              <AccordionContent>
                Legendary is the top rarity tier - ultra-low supply with one-of-a-kind traits and lore. Only ~2% of mints roll Legendary, which is why they command the highest floor.
              </AccordionContent>
            </AccordionItem>
            <AccordionItem value="r-which">
              <AccordionTrigger className="text-left">Which NFT type is the highest rank?</AccordionTrigger>
              <AccordionContent>
                Legendary is the highest rank, followed by Epic, then Rare, then Common. The lower the supply, the higher the rank and the stronger the demand on the secondary market.
              </AccordionContent>
            </AccordionItem>
          </Accordion>
        </section>
      </div>

      <Footer />
    </div>
  );
}

const TILTS = ["-rotate-[8deg] sm:-rotate-[10deg]", "-rotate-[2deg] sm:-rotate-[3deg]", "rotate-[3deg] sm:rotate-[4deg]", "rotate-[9deg] sm:rotate-[11deg]"] as const;
const OFFSETS = ["mt-4 sm:mt-10", "mt-1 sm:mt-4", "-mt-1 sm:-mt-4", "mt-2 sm:mt-8"] as const;

function RarityCard({ r, index }: { r: (typeof RARITIES)[number]; index: number }) {
  const tilt = TILTS[index] ?? "";
  const offset = OFFSETS[index] ?? "";
  return (
    <div
      className={`group relative basis-[24%] shrink-0 min-w-0 transition-transform duration-500 hover:!rotate-0 hover:-translate-y-2 hover:z-20 ${tilt} ${offset}`}
      style={{ zIndex: 10 - Math.abs(index - 1.5) }}
    >
      <Card className="relative overflow-hidden bg-card/60 neon-border rounded-lg sm:rounded-xl shadow-[0_10px_40px_-10px_hsl(var(--primary)/0.6)]">
        <div className="relative aspect-[2/3] overflow-hidden">
          <img
            src={r.image}
            alt={`${r.tier} NFT card`}
            loading="lazy"
            width={400}
            height={600}
            className="absolute inset-0 h-full w-full object-cover transition-transform duration-700 group-hover:scale-105"
          />
          {/* holographic sheen */}
          <div className="pointer-events-none absolute inset-0 bg-gradient-to-tr from-transparent via-white/10 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-500" />
          <div className="pointer-events-none absolute -inset-x-10 -top-10 h-24 rotate-12 bg-gradient-to-r from-transparent via-white/25 to-transparent translate-x-[-120%] group-hover:translate-x-[120%] transition-transform duration-1000 ease-out" />
          {/* iridescent border glow */}
          <div className="pointer-events-none absolute inset-0 rounded-lg sm:rounded-xl ring-1 ring-inset ring-white/10" />
          {/* top label bar */}
          <div className="absolute top-0 inset-x-0 flex items-center justify-between px-1 sm:px-2 py-0.5 sm:py-1 bg-gradient-to-r from-primary/70 via-fuchsia-500/50 to-primary/70 backdrop-blur-sm">
            <span className="text-[7px] sm:text-[10px] md:text-xs font-black text-white tracking-wider drop-shadow truncate">{r.tier.toUpperCase()}</span>
            <span className={`text-[6px] sm:text-[9px] md:text-[10px] font-bold ${r.accent} bg-background/60 rounded-full px-1 sm:px-1.5 shrink-0`}>{r.odds}</span>
          </div>
          {/* bottom info strip */}
          <div className="absolute bottom-0 inset-x-0 px-1 sm:px-2 py-0.5 sm:py-1 bg-background/70 backdrop-blur-sm border-t border-white/10">
            <span className="block text-center text-[6px] sm:text-[9px] md:text-[10px] uppercase tracking-wider text-muted-foreground truncate">{r.supply}</span>
          </div>
        </div>
      </Card>
    </div>
  );
}




function Feature({ icon: Icon, title, desc }: any) {
  return (
    <Card className="p-5 bg-card/60 hover:neon-border transition">
      <Icon className="h-6 w-6 text-primary" />
      <h3 className="mt-3 font-semibold">{title}</h3>
      <p className="mt-1 text-sm text-muted-foreground">{desc}</p>
    </Card>
  );
}
